import { useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { ApiError, applyMe, getMe, logout, SIGN_IN_URL, type Me } from "./api";
import { Layout } from "./Layout";
import { retiredPageHref, type RetiredPage } from "./routes";
import { Eyebrow, Panel, PanelPrimary } from "./SignIn";
import { AssignmentsTab } from "./tabs/Assignments";
import { GradingPage, SocialPage } from "./tabs/Config";
import { HealthTab } from "./tabs/Health";
import { RunsTab } from "./tabs/Runs";
import { StudentsTab } from "./tabs/Students";
import { SubmissionPage } from "./tabs/Submission";
import { ApplyPage } from "./pages/Apply";
import { LogsPage } from "./pages/Logs";
import { ReviewsPage } from "./pages/Reviews";
import { ApplicantsPage } from "./pages/Applicants";
import { TeamsPage } from "./pages/Projects";
import { ApplicantFormPreviewPage } from "./pages/ApplicantFormPreview";
import { ProjectFormPreviewPage } from "./pages/ProjectFormPreview";
import { StudentPreviewPage } from "./pages/StudentPreview";
import { OverviewPage } from "./pages/Overview";
import { TeachersPage } from "./pages/Teachers";
import { DemoPage } from "./pages/Demo";
import { ErrorBox, ThemeToggle } from "./ui";

/**
 * 🔴 `data-tb-inflight` IS A CONTRACT, NOT A DEBUG AID, and it survived both migrations on
 * purpose. A heading renders before its data, so grepping rendered text once reported PASS
 * against a hung deployment. `useAsync` owns the counter; this only initialises it, so a
 * browser check that reads the attribute before the first fetch sees a number rather than
 * `undefined`.
 */
export function App() {
  useEffect(() => {
    if (document.documentElement.dataset.tbInflight == null) {
      document.documentElement.dataset.tbInflight = "0";
    }
  }, []);

  // 🔴 `/apply` IS ROUTED BEFORE THE TEACHER SHELL BOOTS, AND IT HAS TO BE A ROUTE RATHER
  // THAN A BRANCH. `TeacherApp` calls `getMe()` — the teacher endpoint — in an effect, and a
  // hook cannot be made conditional, so the only way a student never triggers it is for that
  // component never to mount. Before this, a student who signed in perfectly landed on
  // `/apply/<id>`, got a 401 from an endpoint meant for somebody else, and was shown a card
  // telling them to sign in with the account that teaches the cohort.
  return (
    <Routes>
      <Route path="/apply" element={<ApplyPage />} />
      <Route path="/apply/:courseId" element={<ApplyPage />} />
      {/* 🔴 A LINK PER ROUND, BECAUSE THE COMBINED PAGE SHOWS WHICHEVER IS OPEN. [X] The
          Teams page's Copy link pointed at `/apply/<id>`, and with the project form off that
          renders the INTAKE questions and nothing else — so a teacher checking the teams
          form was shown the applicants one, and a student sent that link to fill in their
          project preferences would have been too. The bare path still shows both, which is
          what a student who was simply sent "the link" should get. */}
      <Route path="/apply/:courseId/:round" element={<ApplyPage />} />
      <Route path="*" element={<TeacherApp />} />
    </Routes>
  );
}

/** The console proper. Everything from here down is for somebody with a `teachers` row. */
function TeacherApp() {
  const [me, setMe] = useState<Me | null>(null);
  const [err, setErr] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);
  const [student, setStudent] = useState(false);

  // The callback redirects here with a reason when sign-in fails. Showing it on the page
  // beats a JSON body rendered as raw text on what is, to the reader, a login screen.
  const [signinError] = useState(() =>
    new URLSearchParams(window.location.search).get("signin_error"));

  const load = useCallback(() => {
    setLoading(true);
    getMe()
      .then((m) => { setMe(m); setErr(null); })
      .catch(async (e) => {
        const failed = e as ApiError;
        // 🔴 A STUDENT SESSION AND NO TEACHER ONE IS A STUDENT (owner, 2026-09-28): *"if a
        // student it should show the view of the student their profile!!"*. The bare domain
        // is this shell's, so a student who had signed in once was shown the console's door
        // on every visit. `/api/apply/me` answers only for a student session, so a 200 IS one.
        // ⚠️ NOT FOR SOMEBODY WHO ALSO TEACHES — `is_teacher` is what stops a teacher who has
        // tested the student side being sent there every time they open the console — and
        // never past a sign-in refusal, which is the one screen that says what went wrong.
        if (failed.kind === "auth" && !signinError) {
          const mine = await applyMe().catch(() => null);
          if (mine && !mine.is_teacher) {
            setStudent(true);
            return;
          }
        }
        setMe(null); setErr(failed);
      })
      .finally(() => setLoading(false));
  }, [signinError]);

  useEffect(() => {
    load();
    if (signinError) window.history.replaceState({}, "", window.location.pathname);
  }, [load, signinError]);

  if (loading && !me) return <Plain><p className="muted">Loading…</p></Plain>;
  // ⚠️ A REDIRECT RATHER THAN RENDERING THEIR PAGE HERE: `/apply` is where that page lives,
  // and a student who bookmarks what they are looking at must bookmark that.
  if (student) return <Navigate to="/apply" replace />;

  // 🔴 THE CONSOLE'S OWN DOOR, IN THE CONSOLE'S OWN WORDS. This screen used to be the
  // applicant's — "sign in to apply to a cohort" — shown to a teacher. The sentences below are
  // unchanged; what changed is that a student no longer gets them and this reader no longer
  // gets a student's.
  if (signinError || err?.kind === "auth") {
    return (
      <Plain>
        <Panel>
          <div>
            <Eyebrow>Turbine console</Eyebrow>
            <h2>Sign in</h2>
          </div>
          {/* 🔴 BOTH READERS, BECAUSE BOTH ARRIVE HERE (owner, 2026-09-28). The bare domain is
              where students are sent, and since today signing in on this card takes a student
              to their own page — so asking only for "the account that teaches the cohort"
              told most of its readers to go away. */}
          <p className="panel-lede">
            Sign in with Google. Teachers get the console, which grades Google Classroom
            submissions; students get their own page — their team, their assignments and their
            marks.
          </p>
          {/* ⚠️ THE SERVER'S SENTENCE, ON THE PAGE. `App.tsx` has always argued for this: it
              "beats a JSON body rendered as raw text on what is, to the reader, a login
              screen." REFUSED is a word, never a tint alone. */}
          {signinError && (
            <div className="panel-refused" role="alert">
              <span className="tag">REFUSED</span>
              <p className="t">{signinError}</p>
            </div>)}
          <PanelPrimary href={SIGN_IN_URL}>
            {signinError ? "Try again" : "Continue with Google"}
          </PanelPrimary>
          <p className="panel-fine">
            Signing in asks only for your name and email address. Reading Classroom is done by
            the bot credential, separately.
          </p>
          {/* ⚠️ "Are you a student? Your page is at /apply" WAS HERE (2026-09-24), and the
              owner found it was not enough: a student still pressed the button above and was
              told they are not a teacher. The button itself is the student's way in now —
              the sign-in sends an address with no `teachers` row to its own page — so the
              line pointed somewhere the button already goes. */}
        </Panel>
      </Plain>
    );
  }

  if (err) return <Plain><ErrorBox err={err} onRetry={load} /></Plain>;

  // 🔴 A REFUSAL WITH NO EXIT, SHOWN TO THE READER MOST LIKELY TO BE CONFUSED. The sentence
  // was right and there was nothing to press: somebody who signed in with the wrong one of
  // their Google accounts had no way back except clearing cookies. The copy is verbatim; the
  // two additions are the way out and the sentence naming who can fix it.
  // ⚠️ NO SCANLINE — this is a refusal, not a door.
  if (!me || me.courses.length === 0) {
    return (
      <Plain>
        <Panel scan={false}>
          <div>
            <Eyebrow tone="warn">Signed in · no access</Eyebrow>
            <h2 className="tight">No cohort to show you</h2>
          </div>
          <p className="panel-para soft" role="alert">
            <span className="addr">{me?.email}</span> is signed in but is not recorded as a
            teacher of any course this console serves. That is a refusal, not an empty cohort —
            nothing was read.
          </p>
          <div className="panel-acts">
            <button className="panel-act" onClick={() => void logout().then(load)}>
              Sign in as someone else</button>
          </div>
          <p className="panel-fine">
            If you should have access, whoever administers this console adds a
            {" "}<code>teachers</code> row against this address.
          </p>
          {/* ⚠️ THE LIKELIEST READER OF THIS SENTENCE IS NOT A TEACHER AT ALL. Teaching a
              cohort in Classroom grants nothing here, so the refusal is correct — but a
              student who opened the bare domain meets it too, and for them the remedy is not
              an administrator, it is a different address. */}
          <p className="panel-fine">
            If you are a student, this is not your page — yours is at{" "}
            <a href="/apply">/apply</a>.
          </p>
        </Panel>
      </Plain>
    );
  }

  const first = me.courses[0]!;
  const signOut = () => void logout().then(load);

  return (
    <Routes>
      <Route path="/" element={<Navigate to={`/c/${first.id}/overview`} replace />} />
      <Route path="/c/:courseId/*"
             element={<Cohort me={me} onSignOut={signOut} reloadMe={load} />} />
      {/* Anything else is not a page this app has. Send them somewhere real rather than
          rendering a blank shell. */}
      <Route path="*" element={<Navigate to={`/c/${first.id}/overview`} replace />} />
    </Routes>
  );
}

/** Resolves the cohort in the URL exactly once, and hands every page a `courseId: string`.
 *
 * ⚠️ AN ID FROM THE ADDRESS BAR IS NOT TRUSTED. A course this teacher does not teach — or a
 * base64 id pasted from a Classroom URL — falls back to their first cohort rather than
 * rendering an empty shell against a 404 from every route on the page.
 */
function Cohort({ me, onSignOut, reloadMe }:
  { me: Me; onSignOut: () => void; reloadMe: () => void }) {
  const { courseId } = useParams();
  const known = me.courses.find((c) => c.id === courseId);
  const first = me.courses[0]!;
  if (!known) return <Navigate to={`/c/${first.id}/overview`} replace />;

  return (
    <Routes>
      <Route element={<Layout me={me} onSignOut={onSignOut} courseId={known.id}
                              reloadMe={reloadMe} />}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<OverviewPage />} />
        <Route path="assignments" element={<AssignmentsTab />} />
        <Route path="assignments/:cw" element={<AssignmentsTab />} />
        {/* 🔴 GRADES AND ENTRY ARE VIEWS OF ASSIGNMENTS (2026-10-03), AND THEIR ADDRESSES STILL
            RESOLVE. They are in bookmarks and in the sidebar of every tab opened before the
            merge; the cohort's catch-all below would otherwise drop them on the Overview with
            nothing said. */}
        <Route path="grades" element={<ToFilter courseId={known.id} page="grades" />} />
        <Route path="entry" element={<ToFilter courseId={known.id} page="entry" />} />
        <Route path="submissions/:sid" element={<SubmissionPage />} />
        <Route path="students" element={<StudentsTab />} />
        {/* 🔴 `teams` MEANS TEAMS AGAIN. It briefly redirected to Applicants, back when
            that page held the grouping; a link pasted into Discord then now lands on the
            page that actually forms teams, which is the better answer to the same address. */}
        <Route path="teams" element={<Navigate to="questions" replace />} />
        <Route path="teams/:tab" element={<TeamsPage />} />
        <Route path="applicants" element={<Navigate to="applications" replace />} />
        <Route path="applicants/:tab" element={<ApplicantsPage />} />
        <Route path="runs" element={<RunsTab />} />
        <Route path="reviews" element={<ReviewsPage />} />
        <Route path="logs" element={<LogsPage />} />
        <Route path="health" element={<HealthTab />} />
        <Route path="grading" element={<Navigate to="grader" replace />} />
        <Route path="grading/:tab" element={<GradingPage />} />
        <Route path="social" element={<SocialPage />} />
        {/* 🔴 ROUTED FOR EVERYBODY AND REFUSED BY THE SERVER, not hidden by the router. The
            sidebar leaves the link out unless `me.is_owner`, but somebody who types the
            address must meet the 403 with its sentence — a client-side redirect would send
            them back to the Overview with nothing said, which reads as a broken link. */}
        <Route path="teachers" element={<TeachersPage />} />
        {/* ⚠️ ROUTED WHEREVER THE SIDEBAR DRAWS IT OR NOT, like Teachers beside it: a
            deployment that does not allow demo cohorts answers the page with the reason
            rather than a redirect to nowhere. */}
        <Route path="demo" element={<DemoPage />} />
        <Route path="*" element={<Navigate to="overview" replace />} />
      </Route>
      {/* 🔴 OUTSIDE `Layout`, ON PURPOSE. This is the project form as a student sees it, and
          a student has no sidebar, no cohort switcher and no nav counts. Rendering it inside
          the console's chrome would make the one thing it exists to show — what the page
          actually looks like — the one thing it got wrong. It is still behind the teacher
          shell, so it is authorised like every other page here and reaches no `/api/apply`
          route. A static segment outranks `teams/:tab`, so this wins the match. */}
      <Route path="teams/form/preview"
             element={<ProjectFormPreviewPage courseId={known.id} />} />
      <Route path="applicants/form/preview"
             element={<ApplicantFormPreviewPage courseId={known.id} />} />
      {/* ⚠️ OUTSIDE `Layout` FOR THE SAME REASON as the form preview above: this is one
          student's own page, and a student has no sidebar. A static `students/` segment
          followed by two more outranks nothing else here. */}
      <Route path="students/:studentId/preview" element={<StudentPreviewPage />} />
    </Routes>
  );
}

/** A page that became a filter: its address lands on the filter, its query string kept. */
function ToFilter({ courseId, page }: { courseId: string; page: RetiredPage }) {
  const { search } = useLocation();
  return <Navigate to={retiredPageHref(courseId, page, search)} replace />;
}

/** The pre-router screens: sign in, no cohort, a fault. No sidebar, because there is no
 *  cohort to put in it. */
function Plain({ children }: { children: React.ReactNode }) {
  return (
    <div className="shell">
      <header className="topbar">
        {/* ⚠️ NO "grading" TAG. It was accurate when the console did one thing; it now carries
            the roster, the social-activity sweep, the activity log and the health of the whole
            pipeline, and a label that names one of those makes the rest look like visitors. */}
        {/* 🔴 THE WORDMARK, NOT A COLOURED CIRCLE. The console wore an 8px dot in the old
            generic blue beside the word "Turbine" — a placeholder identity that survived the
            2026-09-06 brand work because that pass stopped at the applicant surface. The
            supplied asset is the only brand asset there is; it belongs where the product
            names itself.
            ⚠️ IT STAYS AN `h1`. The dot carried no text, so the topbar's only heading was the
            word beside it; the `alt` gives the same heading its accessible name, and every
            page's own `h2` still sits one level below it. */}
        <h1 className="brand">
          <img className="logo" src="/brand/turbin3-logo.svg" alt="Turbin3"
               width={177} height={35} />
        </h1>
        <span className="spacer" />
        <ThemeToggle />
      </header>
      <main className="stack">{children}</main>
    </div>
  );
}
